import * as React from 'react'
import { TextField } from '@/ui/form/controls/TextField'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useCompanyForm } from '../services/useCompanyForm'
import { useMutateGusData } from '@/modules/settings/react-query/basic/useMutateGusData'

type Props = {
  companyId: string
  onSuccess?: (companyId: string) => void
}

export const ManualCompanyForm: React.FC<Props> = ({ companyId, onSuccess }) => {
  const { t } = useLocalize()
  const {
    formData,
    updateField,
    submit,
    loading,
    error,
    success,
  } = useCompanyForm(companyId)

  const gus = useMutateGusData(companyId)

  const handleCompanyNameChange = (name: string) => {
    updateField('companyName', name)
    if (!formData.invoiceCompanyName) {
      updateField('invoiceCompanyName', name)
    }
  }

  const handleNipChange = async (nip: string) => {
    updateField('nip', nip)
    const cleanNip = nip.replace(/\D/g, '')
    if (cleanNip.length !== 10 || gus.isPending) return
    const res = await gus.mutateAsync({ nip: cleanNip })
    if (res.name) {
      updateField('companyName', res.name)
      if (!formData.invoiceCompanyName) {
        updateField('invoiceCompanyName', res.name)
      }
    }
    if (res.nip)            updateField('nip', res.nip)
    if (res.regon)          updateField('regon', res.regon)
    if (res.krs)            updateField('krs', res.krs)
    if (res.email)          updateField('email', res.email)
    if (res.street)         updateField('street', res.street)
    if (res.buildingNumber) updateField('buildingNumber', res.buildingNumber)
    if (res.flatNumber)     updateField('houseNumber', res.flatNumber)
    if (res.postalCode)     updateField('postalCode', res.postalCode)
    if (res.city)           updateField('city', res.city)
    if (res.officePhone)    updateField('contactPhone', res.officePhone)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const result = await submit()
    if (result.success && result.companyId !== undefined && onSuccess) {
      onSuccess(result.companyId)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
    >
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-6 tw-overflow-y-auto tw-px-4 tw-py-6">
        {error && (
          <div className="tw-rounded-md tw-bg-red-50 tw-border tw-border-red-200 tw-p-3 tw-text-sm tw-text-red-800">
            {error}
          </div>
        )}

        {success && (
          <div className="tw-rounded-md tw-bg-green-50 tw-border tw-border-green-200 tw-p-3 tw-text-sm tw-text-green-800">
            {t('addCompany.companyCreatedSuccess')}
          </div>
        )}

          {/* Basic Information */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            <TextField
              label={t('addCompany.companyName')}
              required
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.companyName}
              onChange={(e) => handleCompanyNameChange(e.target.value)}
            />
            <TextField
              label={t('addCompany.invoiceCompanyName')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.invoiceCompanyName}
              onChange={(e) => updateField('invoiceCompanyName', e.target.value)}
            />
            <TextField
              label={t('addCompany.uniqueIdentifier')}
              required
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.uniqueIdentifier}
              onChange={(e) => updateField('uniqueIdentifier', e.target.value)}
            />
            <div>
              <TextField
                label="NIP"
                placeholder={t('addCompany.typePlaceholder')}
                value={formData.nip}
                onChange={(e) => handleNipChange(e.target.value)}
              />
              {gus.isPending && (
                <p className="tw-mt-1 tw-text-xs tw-text-content-secondary">{t('addCompany.fetchingGusData')}</p>
              )}
            </div>
            <TextField
              label="REGON"
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.regon}
              onChange={(e) => updateField('regon', e.target.value)}
            />
            <TextField
              label="KRS"
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.krs}
              onChange={(e) => updateField('krs', e.target.value)}
            />
          </div>

          {/* Contact Information */}
          <div className="tw-h-[1px] tw-w-full tw-bg-base-300"></div>
          <h4 className="tw-text-md tw-font-semibold">{t('addCompany.contactInfo')}</h4>
          <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Email"
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
            />
            <TextField
              label={t('addCompany.notificationsEmail')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.notificationsEmail}
              onChange={(e) =>
                updateField('notificationsEmail', e.target.value)
              }
            />
            <TextField
              label={t('addCompany.contactEmail')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.contactEmail}
              onChange={(e) => updateField('contactEmail', e.target.value)}
            />
            <TextField
              label={t('addCompany.contactPhone')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.contactPhone}
              onChange={(e) => updateField('contactPhone', e.target.value)}
            />
          </div>

          {/* Address */}
          <div className="tw-h-[1px] tw-w-full tw-bg-base-300"></div>
          <h4 className="tw-text-md tw-font-semibold">{t('common.address')}</h4>
          <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            <TextField
              label={t('addCompany.street')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.street}
              onChange={(e) => updateField('street', e.target.value)}
            />
            <TextField
              label={t('addCompany.houseNumber')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.buildingNumber}
              onChange={(e) => updateField('buildingNumber', e.target.value)}
            />
            <TextField
              label={t('addCompany.flatNumber')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.houseNumber}
              onChange={(e) => updateField('houseNumber', e.target.value)}
            />
            <TextField
              label={t('addCompany.postalCode')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.postalCode}
              onChange={(e) => updateField('postalCode', e.target.value)}
            />
            <TextField
              label={t('common.city')}
              placeholder={t('addCompany.typePlaceholder')}
              value={formData.city}
              onChange={(e) => updateField('city', e.target.value)}
            />
          </div>

      </div>

      <div className="tw-flex tw-justify-end tw-gap-4 tw-border-t tw-border-base-300 tw-bg-white tw-px-4 tw-py-4">
            <button
              type="button"
              onClick={() => {
                // Reset form
                updateField('companyName', '')
                updateField('invoiceCompanyName', '')
                updateField('uniqueIdentifier', '')
                updateField('nip', '')
                updateField('regon', '')
                updateField('krs', '')
                updateField('email', '')
                updateField('notificationsEmail', '')
                updateField('contactEmail', '')
                updateField('contactPhone', '')
                updateField('street', '')
                updateField('houseNumber', '')
                updateField('buildingNumber', '')
                updateField('postalCode', '')
                updateField('city', '')
              }}
              className="tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100"
            >
              {t('addCompany.clear')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.companyName || !formData.uniqueIdentifier}
              className="tw-rounded-md tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {loading ? t('addCompany.creating') : t('addCompany.createCompany')}
            </button>
          </div>
        </form>
  )
}
